import styled from '@emotion/styled/macro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { forTabletPortraitDown, Spinner, Variables } from '../base';

type DashboardEndpoint = 'provider' | 'support';

interface DashboardCoding {
    id?: string;
    requestType?: string;
    status?: string;
    vendor?: string;
    notes?: string;
    claimId?: string;
    userId?: string;
    hl7Id?: string;
    request?: string;
    tags?: unknown[];
    created?: string;
    before?: unknown;
    after?: unknown;
    generatedBefore?: unknown;
    generatedAfter?: unknown;
    generated_before?: unknown;
    generated_after?: unknown;
}

export interface CoderRule {
    ownerId?: string;
    ownerName?: string;
    ruleKey?: string;
    ruleNumber?: number;
    description?: string;
    isActive?: boolean;
    created?: string;
}

export interface ClaimTransmissionStatus {
    id?: string;
    externalId?: string;
    status?: string;
    fields?: string;
    message?: string;
    messageCode?: string;
    messages?: string;
    created?: string;
}

export interface ClaimTransmission {
    id?: string;
    statuses?: ClaimTransmissionStatus[];
    created?: string;
}

export interface CoderRuleClaim {
    claimId: string;
    runDate?: string;
}

export interface LatestTransmissionStatus {
    transmissionId?: string;
    status?: string;
    message?: string;
    created?: string;
}

interface ClaimRuleActivity {
    claimId: string;
    userId?: string;
    codingId?: string;
    codingCreated?: string;
    runDate?: string;
    latestTransmission?: LatestTransmissionStatus | null;
}

export interface CoderRuleSummary extends CoderRule {
    claims: ClaimRuleActivity[];
    failedCount: number;
    latestRunDate?: string;
    missingTransmissionCount: number;
    pendingCount: number;
    successCount: number;
    successRate: number | null;
    transmittedClaimCount: number;
}

interface CoderRulesDashboardItem {
    ruleNumber?: number | string;
    ruleDescription?: string;
    claimId?: string;
    runDate?: string;
    transmissionId?: string;
    transmissionCreateDate?: string;
    latestTransmissionStatus?: string;
    latestTransmissionStatusMessage?: string;
}

interface TokenResponse {
    access_token?: string;
    refresh_token?: string;
    token_type?: string;
    expires_in?: number;
}

interface CoderRulesDashboardProps {
    accessToken?: string;
    autoLoad?: boolean;
    apiBaseUrl?: string;
    clientId?: string;
    clientSecret?: string;
    endpoint?: DashboardEndpoint;
    getAccessToken?: () => string | undefined;
    showConnectionControls?: boolean;
    userId?: string;
    userName?: string;
}

interface FetchApiCoderRulesDashboardOptions {
    accessToken?: string;
    apiBaseUrl: string;
    clientId?: string;
    clientSecret?: string;
    endpoint: DashboardEndpoint;
    userId?: string;
    userName?: string;
    onDashboardUpdate?: (dashboard: ApiCoderRulesDashboardResponse) => void;
    onProgress?: (message: string) => void;
}

interface FetchLatestCodingsForClaimsOptions {
    batchSize?: number;
    concurrency?: number;
}

interface FetchLatestTransmissionStatusesForClaimsOptions {
    batchSize?: number;
    concurrency?: number;
    fallbackConcurrency?: number;
}

interface FetchLatestTransmissionStatusesBatchResult {
    isPageFull: boolean;
    latestTransmissionsByClaimId: Map<string, LatestTransmissionStatus | null>;
    matchedClaimIds: Set<string>;
    transmissionCount: number;
}

interface ApiCoderRulesDashboardResponse {
    activeRuleCount?: number;
    generatedAt?: string;
    matchedClaimCount?: number;
    overallSuccessRate?: number | null;
    overallTransmittedCount?: number;
    ruleCount?: number;
    rules?: CoderRuleSummary[];
    sampleSize?: number;
}

interface CoderRulesDashboardCacheParams {
    apiBaseUrl: string;
    endpoint: DashboardEndpoint;
    userId?: string;
    userName?: string;
}

interface CoderRulesDashboardCacheEntry {
    cacheKey: string;
    dashboard: ApiCoderRulesDashboardResponse & { rules: CoderRuleSummary[] };
    savedAt: string;
    version: number;
}

function getDefaultApiBaseUrl() {
    if (typeof window !== 'undefined' && window.location.host.includes('-web')) {
        return '/api';
    }

    return process.env.REACT_APP_API_URL || process.env.REACT_APP_ENTER_API_BASE_URL || 'https://api.enter.health';
}

const DEFAULT_API_BASE_URL = getDefaultApiBaseUrl();
const DEFAULT_CLIENT_ID = process.env.REACT_APP_ENTER_API_CLIENT_ID || '';
const DEFAULT_CLIENT_SECRET = process.env.REACT_APP_ENTER_API_CLIENT_SECRET || '';
const DEFAULT_ENDPOINT: DashboardEndpoint =
    process.env.REACT_APP_ENTER_API_ENDPOINT === 'provider' ? 'provider' : 'support';
const DEFAULT_USER_ID = process.env.REACT_APP_ENTER_API_USER_ID || '';
const DEFAULT_USER_NAME = process.env.REACT_APP_ENTER_API_USER_NAME || 'Current API user';
const RULE_CLAIM_SAMPLE_SIZE = 10;
const CODING_PAGE_LIMIT = 100;
const TRANSMISSION_PAGE_LIMIT = 100;
const RULE_CLAIM_CONCURRENCY = 3;
const CLAIM_CODING_BATCH_SIZE = 25;
const CLAIM_CODING_CONCURRENCY = 5;
const CODING_BATCH_ERROR_DELAY_MS = 5000;
const TRANSMISSION_CLAIM_BATCH_SIZE = 20;
const TRANSMISSION_CONCURRENCY = 4;
const TRANSMISSION_FALLBACK_CONCURRENCY = 4;
const TRANSMISSION_RETRY_COUNT = 2;
const TRANSMISSION_RETRY_DELAY_MS = 700;
const TRANSMISSION_BATCH_ERROR_DELAY_MS = 5000;
const RULE_CLAIM_RETRY_COUNT = 2;
const RULE_CLAIM_RETRY_DELAY_MS = 700;
const CLAIM_MINIMUM_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const SUCCESS_TRANSMISSION_STATUSES = new Set(['ACKNOWLEDGED', 'ADJUDICATED', 'COMPLETED', 'FORWARDED']);
const FAILED_TRANSMISSION_STATUSES = new Set(['DENIED', 'REJECTED']);
const CODER_RULES_DASHBOARD_CACHE_KEY = 'coder_rules_dashboard_cache';
const CODER_RULES_DASHBOARD_CACHE_VERSION = 5;

const DashboardShell = styled.div`
    color: ${Variables.Colors.n100};

    * {
        box-sizing: border-box;
    }

    .tab-panel {
        display: flex;
        flex-direction: column;
        gap: 24px;
        min-width: 0;
    }

    .topbar,
    .connection-band,
    .table-section {
        border: 1px solid ${Variables.Colors.platinum};
        border-radius: 8px;
        background: ${Variables.Colors.white};
    }

    .topbar {
        display: flex;
        justify-content: space-between;
        gap: 24px;
        align-items: center;
        padding: 24px;
    }

    .topbar-actions {
        display: flex;
        flex: 0 0 auto;
        align-items: center;
    }

    .eyebrow {
        margin: 0 0 8px;
        color: ${Variables.Colors.n50};
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0;
        text-transform: uppercase;
    }

    h1,
    h2,
    p {
        margin-top: 0;
    }

    h1 {
        margin-bottom: 0;
        color: ${Variables.Colors.n100};
        font-size: 28px;
        font-weight: 600;
        line-height: 1.2;
    }

    h2 {
        margin-bottom: 8px;
        color: ${Variables.Colors.n100};
        font-size: 20px;
        font-weight: 600;
    }

    .connection-state,
    .summary-card {
        border: 1px solid ${Variables.Colors.platinum};
        border-radius: 8px;
        background: ${Variables.Colors.p50};
    }

    .summary-card span,
    .summary-card small,
    .connection-state span,
    .rate-breakdown,
    .rule-claim-card span {
        color: ${Variables.Colors.n50};
        font-size: 12px;
        line-height: 1.35;
    }

    .summary-card strong,
    .connection-state strong {
        display: block;
        color: ${Variables.Colors.n100};
        font-size: 15px;
        line-height: 1.35;
    }

    .connection-band {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(220px, 280px);
        gap: 20px;
        padding: 20px;
    }

    .connection-form {
        display: grid;
        grid-template-columns: repeat(4, minmax(160px, 1fr));
        gap: 16px;
        align-items: end;
    }

    .connection-form.compact {
        grid-template-columns: minmax(220px, 1fr) auto;
        align-items: center;
    }

    label {
        display: flex;
        flex-direction: column;
        gap: 6px;
        color: ${Variables.Colors.n70};
        font-size: 13px;
        font-weight: 600;
    }

    input,
    select {
        width: 100%;
        min-height: 40px;
        border: 1px solid ${Variables.Colors.platinum};
        border-radius: 6px;
        background: ${Variables.Colors.white};
        color: ${Variables.Colors.n100};
        font: inherit;
        padding: 0 12px;
    }

    input:focus,
    select:focus,
    button:focus {
        outline: 2px solid ${Variables.Colors.b300};
        outline-offset: 2px;
    }

    .actions {
        display: flex;
        justify-content: flex-end;
    }

    button {
        min-height: 40px;
        border: 0;
        border-radius: 6px;
        background: ${Variables.Colors.p500};
        color: ${Variables.Colors.white};
        cursor: pointer;
        font-size: 14px;
        font-weight: 600;
        padding: 0 16px;
        white-space: nowrap;
    }

    button:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }

    .secondary-button {
        border: 1px solid ${Variables.Colors.platinum};
        background: ${Variables.Colors.white};
        color: ${Variables.Colors.p500};
    }

    .compact-button {
        min-height: 32px;
        padding: 0 10px;
    }

    .connection-state {
        display: flex;
        gap: 12px;
        align-items: center;
        padding: 14px;
    }

    .state-dot {
        width: 10px;
        height: 10px;
        flex: 0 0 10px;
        border-radius: 50%;
        background: ${Variables.Colors.n30};
    }

    .state-dot.ready {
        background: ${Variables.Colors.successful};
    }

    .message {
        border: 1px solid ${Variables.Colors.platinum};
        border-radius: 8px;
        background: ${Variables.Colors.p50};
        color: ${Variables.Colors.n70};
        padding: 14px 16px;
    }

    .dashboard-note {
        border-color: ${Variables.Colors.b200};
        background: ${Variables.Colors.b50};
    }

    .message-content {
        display: inline-flex;
        gap: 8px;
        align-items: center;
    }

    .message-spinner {
        flex: 0 0 auto;
    }

    .error-message {
        border-color: ${Variables.Colors.warning};
        background: #fff5f6;
        color: ${Variables.Colors.warning};
    }

    .summary-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 16px;
    }

    .summary-card {
        padding: 18px;
    }

    .summary-card strong {
        margin: 6px 0;
        font-size: 24px;
    }

    .summary-card .date-value {
        font-size: 16px;
    }

    .table-section {
        overflow: hidden;
    }

    .table-header {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        padding: 20px 24px;
        border-bottom: 1px solid ${Variables.Colors.platinum};
    }

    .table-header p {
        max-width: 820px;
        margin-bottom: 0;
        color: ${Variables.Colors.n70};
        font-size: 14px;
        line-height: 1.5;
    }

    .table-wrap {
        overflow-x: auto;
    }

    .rules-table {
        width: 100%;
        min-width: 1080px;
        border-collapse: collapse;
    }

    .rules-table th,
    .rules-table td {
        border-bottom: 1px solid ${Variables.Colors.platinum};
        padding: 14px 16px;
        text-align: left;
        vertical-align: top;
    }

    .rules-table th {
        background: ${Variables.Colors.p50};
        color: ${Variables.Colors.n50};
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
    }

    .rules-table td {
        color: ${Variables.Colors.n100};
        font-size: 14px;
        line-height: 1.45;
    }

    .empty-state {
        color: ${Variables.Colors.n50};
        text-align: center;
    }

    .rule-number-cell {
        font-weight: 700;
    }

    .rate-breakdown {
        display: block;
        margin-top: 4px;
    }

    .rule-description-cell {
        min-width: 260px;
        max-width: 420px;
    }

    .status-pill {
        display: inline-flex;
        align-items: center;
        min-height: 24px;
        border-radius: 999px;
        background: ${Variables.Colors.n15_24};
        color: ${Variables.Colors.n70};
        font-size: 11px;
        font-weight: 700;
        padding: 0 8px;
    }

    .status-success,
    .status-completed {
        background: rgba(112, 212, 190, 0.22);
        color: #246f61;
    }

    .status-failed {
        background: #fff5f6;
        color: ${Variables.Colors.warning};
    }

    .status-missing,
    .status-pending {
        background: #fff8e6;
        color: #8f6500;
    }

    .status-submitted {
        background: ${Variables.Colors.n15_24};
        color: ${Variables.Colors.n70};
    }

    .rule-claims-row td {
        background: #fbfcfe;
    }

    .rule-claims-toolbar {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        align-items: flex-start;
        margin-bottom: 12px;
    }

    .status-filter-label {
        display: block;
        margin-bottom: 8px;
        color: ${Variables.Colors.n50};
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
    }

    .status-filter-group {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    button.status-filter-pill {
        min-height: 28px;
        border: 1px solid transparent;
        padding: 0 10px;
        opacity: 0.5;
    }

    button.status-filter-pill.selected {
        border-color: currentColor;
        opacity: 1;
    }

    .status-filter-count {
        margin-left: 6px;
        color: inherit;
        font-size: 10px;
        opacity: 0.75;
    }

    .rule-claims-list {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 12px;
    }

    .rule-claim-card {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
        border: 1px solid ${Variables.Colors.platinum};
        border-radius: 8px;
        background: ${Variables.Colors.white};
        padding: 14px;
    }

    .rule-claim-card strong {
        display: block;
        overflow-wrap: anywhere;
        color: ${Variables.Colors.n100};
        font-size: 13px;
        line-height: 1.4;
    }

    .rule-claim-card a {
        color: ${Variables.Colors.p500};
    }

    .rule-claim-message {
        grid-column: 1 / -1;
    }

    .rule-claims-empty {
        border: 1px dashed ${Variables.Colors.platinum};
        border-radius: 8px;
        background: ${Variables.Colors.white};
        color: ${Variables.Colors.n50};
        padding: 18px;
        text-align: center;
    }

    ${forTabletPortraitDown(`
    .topbar,
    .connection-band {
      grid-template-columns: 1fr;
      flex-direction: column;
    }

    .connection-form,
    .connection-form.compact,
    .summary-grid {
      grid-template-columns: 1fr;
    }

    .topbar {
      padding: 18px;
    }

    h1 {
      font-size: 24px;
    }
  `)}
`;

function normalizeBaseUrl(apiBaseUrl: string) {
    return apiBaseUrl.trim().replace(/\/+$/, '');
}

function buildApiUrl(apiBaseUrl: string, path: string) {
    return `${normalizeBaseUrl(apiBaseUrl)}${path}`;
}

function delay(ms: number) {
    return new Promise(resolve => {
        globalThis.setTimeout(resolve, ms);
    });
}

function isRetryableStatus(status: number) {
    return true;
}

async function readError(response: Response) {
    const text = await response.text();

    if (!text) {
        return `${response.status} ${response.statusText}`;
    }

    try {
        const payload = JSON.parse(text);
        const message = payload.error_description || payload.error || payload.message || payload.detail || text;

        return `${response.status} ${response.statusText}: ${message}`;
    } catch {
        return `${response.status} ${response.statusText}: ${text}`;
    }
}

function getCoderRulesDashboardStorage() {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

function buildCoderRulesDashboardCacheKey({ apiBaseUrl, endpoint, userId, userName }: CoderRulesDashboardCacheParams) {
    return JSON.stringify({
        apiBaseUrl: apiBaseUrl.trim(),
        endpoint,
        userId: userId?.trim() || '',
        userName: userName?.trim() || DEFAULT_USER_NAME,
    });
}

function isCachedCoderRulesDashboardEntry(value: unknown): value is CoderRulesDashboardCacheEntry {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const entry = value as Partial<CoderRulesDashboardCacheEntry>;

    return (
        entry.version === CODER_RULES_DASHBOARD_CACHE_VERSION &&
        typeof entry.cacheKey === 'string' &&
        typeof entry.savedAt === 'string' &&
        !!entry.dashboard &&
        Array.isArray(entry.dashboard.rules)
    );
}

function readCachedCoderRulesDashboard(cacheKey: string) {
    const storage = getCoderRulesDashboardStorage();

    if (!storage) {
        return null;
    }

    try {
        const cachedDashboard = storage.getItem(CODER_RULES_DASHBOARD_CACHE_KEY);

        if (!cachedDashboard) {
            return null;
        }

        const entry: unknown = JSON.parse(cachedDashboard);

        if (!isCachedCoderRulesDashboardEntry(entry) || entry.cacheKey !== cacheKey) {
            return null;
        }

        return entry;
    } catch {
        return null;
    }
}

function saveCachedCoderRulesDashboard(cacheKey: string, dashboard: ApiCoderRulesDashboardResponse) {
    const storage = getCoderRulesDashboardStorage();

    if (!storage || !dashboard.rules) {
        return;
    }

    try {
        storage.setItem(
            CODER_RULES_DASHBOARD_CACHE_KEY,
            JSON.stringify({
                cacheKey,
                dashboard: {
                    ...dashboard,
                    rules: dashboard.rules,
                },
                savedAt: new Date().toISOString(),
                version: CODER_RULES_DASHBOARD_CACHE_VERSION,
            } satisfies CoderRulesDashboardCacheEntry),
        );
    } catch {
        // localStorage may be unavailable or full. Loading from the API should still succeed.
    }
}

async function requestAccessToken(apiBaseUrl: string, clientId: string, clientSecret: string) {
    const body = new URLSearchParams({
        grant_type: 'client_credentials',
        client_id: clientId,
        client_secret: clientSecret,
    });

    const response = await fetch(buildApiUrl(apiBaseUrl, '/oauth/token'), {
        method: 'POST',
        headers: {
            accept: 'application/json',
            'content-type': 'application/x-www-form-urlencoded',
        },
        body,
    });

    if (!response.ok) {
        throw new Error(await readError(response));
    }

    const payload = (await response.json()) as TokenResponse;

    if (!payload.access_token) {
        throw new Error('Token response did not include an access token.');
    }

    return payload.access_token;
}

function getCreatedTime(value?: string) {
    if (!value) {
        return 0;
    }

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function getMinimumClaimCreatedTime(now = Date.now()) {
    return now - CLAIM_MINIMUM_AGE_MS;
}

function isClaimAtLeastAWeekOld(codingCreated?: string, now = Date.now()) {
    const createdTime = getCreatedTime(codingCreated);
    return createdTime > 0 && createdTime <= getMinimumClaimCreatedTime(now);
}

function formatDate(value?: string) {
    if (!value) {
        return 'Unknown';
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(parsed);
}

function parseDashboardLoadedAt(value?: string) {
    if (!value) {
        return null;
    }

    const parsed = new Date(value);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatPercent(value: number | null, isLoadingTransmissions = false) {
    if (value !== null) {
        return `${Math.round(value)}%`;
    }

    return isLoadingTransmissions ? 'Loading transmissions' : 'No transmissions';
}

function buildClaimSupportUrl(userId: string, claimId: string) {
    const currentOrigin = typeof window === 'undefined' ? '' : window.location.origin;

    return `${currentOrigin}/${encodeURIComponent(userId)}/claims/${encodeURIComponent(claimId)}`;
}

function statusClassName(status: string) {
    const normalizedStatus = status.toUpperCase();

    if (SUCCESS_TRANSMISSION_STATUSES.has(normalizedStatus)) {
        return 'status-pill status-success';
    }

    if (FAILED_TRANSMISSION_STATUSES.has(normalizedStatus)) {
        return 'status-pill status-failed';
    }

    if (normalizedStatus === 'NO_STATUS') {
        return 'status-pill status-missing';
    }

    return 'status-pill status-submitted';
}

function formatTransmissionStatusLabel(status: string) {
    return status === 'NO_STATUS' ? 'MISSING' : status;
}

function getClaimTransmissionStatus(claim: ClaimRuleActivity) {
    return (claim.latestTransmission?.status?.trim() || 'NO_STATUS').toUpperCase();
}

function getClaimStatusOptions(claims: ClaimRuleActivity[]) {
    const statusCounts = new Map<string, number>();

    claims.forEach(claim => {
        const status = getClaimTransmissionStatus(claim);

        statusCounts.set(status, (statusCounts.get(status) || 0) + 1);
    });

    return Array.from(statusCounts.entries()).map(([status, count]) => ({ count, status }));
}

function normalizeTransmissionMessage(...values: Array<string | undefined>) {
    const trimmedValue = values.find(value => value?.trim())?.trim();

    return trimmedValue || undefined;
}

function coerceRuleNumber(value: unknown) {
    if (typeof value === 'number' && Number.isInteger(value)) {
        return value;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const trimmedValue = value.trim();

    if (!/^\d+$/.test(trimmedValue)) {
        return null;
    }

    return Number(trimmedValue);
}

function parseMaybeJson(value: unknown) {
    if (typeof value !== 'string') {
        return value;
    }

    const trimmedValue = value.trim();

    if (!trimmedValue || !['{', '['].includes(trimmedValue[0])) {
        return value;
    }

    try {
        return JSON.parse(trimmedValue);
    } catch {
        return value;
    }
}

function getStringValuesByKey(value: unknown, keyName: string, values = new Set<string>()) {
    const parsedValue = parseMaybeJson(value);

    if (Array.isArray(parsedValue)) {
        parsedValue.forEach(item => getStringValuesByKey(item, keyName, values));
        return values;
    }

    if (typeof parsedValue !== 'object' || parsedValue === null) {
        return values;
    }

    Object.entries(parsedValue as Record<string, unknown>).forEach(([key, childValue]) => {
        if (key === keyName && typeof childValue === 'string' && childValue.trim()) {
            values.add(childValue.trim());
        }

        getStringValuesByKey(childValue, keyName, values);
    });

    return values;
}

function collectRuleNumbers(value: unknown, ruleNumbers: Set<number>, parentKey = '') {
    const parsedValue = parseMaybeJson(value);

    if (typeof parsedValue === 'string') {
        const pattern = /(?:coder\s*)?rule(?:\s*number|\s*#|\s+)?\s*(\d+)/gi;
        let match = pattern.exec(parsedValue);

        while (match) {
            ruleNumbers.add(Number(match[1]));
            match = pattern.exec(parsedValue);
        }

        return;
    }

    if (Array.isArray(parsedValue)) {
        parsedValue.forEach(item => collectRuleNumbers(item, ruleNumbers, parentKey));
        return;
    }

    if (typeof parsedValue !== 'object' || parsedValue === null) {
        return;
    }

    Object.entries(parsedValue as Record<string, unknown>).forEach(([key, childValue]) => {
        if (/^(coderRuleNumber|codingRuleNumber|ruleNumber)$/i.test(key)) {
            const ruleNumber = coerceRuleNumber(childValue);

            if (ruleNumber !== null) {
                ruleNumbers.add(ruleNumber);
            }
        }

        collectRuleNumbers(childValue, ruleNumbers, key);
    });
}

function getCodingUserId(coding: DashboardCoding) {
    const directUserId = coding.userId?.trim();

    if (directUserId) {
        return directUserId;
    }

    return Array.from(getStringValuesByKey(coding, 'userId'))[0];
}

export function extractRuleNumbersFromCoding(coding: DashboardCoding) {
    const ruleNumbers = new Set<number>();

    collectRuleNumbers(
        {
            notes: coding.notes,
            request: coding.request,
            tags: coding.tags,
            before: coding.before,
            after: coding.after,
            generatedBefore: coding.generatedBefore ?? coding.generated_before,
            generatedAfter: coding.generatedAfter ?? coding.generated_after,
        },
        ruleNumbers,
    );

    return Array.from(ruleNumbers).sort((left, right) => left - right);
}

export function getLatestTransmissionStatus(transmissions: ClaimTransmission[]): LatestTransmissionStatus | null {
    let latest: LatestTransmissionStatus | null = null;
    let latestTime = 0;

    transmissions.forEach(transmission => {
        const statuses = Array.isArray(transmission.statuses) ? transmission.statuses : [];

        if (statuses.length === 0) {
            const transmissionTime = getCreatedTime(transmission.created);

            if (!latest || transmissionTime > latestTime) {
                latest = {
                    transmissionId: transmission.id,
                    created: transmission.created,
                };
                latestTime = transmissionTime;
            }

            return;
        }

        statuses.forEach(status => {
            const statusTime = getCreatedTime(status.created) || getCreatedTime(transmission.created);

            if (!latest || statusTime > latestTime) {
                latest = {
                    transmissionId: transmission.id,
                    status: status.status,
                    message: normalizeTransmissionMessage(status.messages, status.message),
                    created: status.created || transmission.created,
                };
                latestTime = statusTime;
            }
        });
    });

    return latest;
}

function mergeLatestTransmissionStatus(
    latestTransmissionsByClaimId: Map<string, LatestTransmissionStatus | null>,
    claimId: string,
    latestTransmission: LatestTransmissionStatus | null,
) {
    if (!latestTransmission) {
        if (!latestTransmissionsByClaimId.has(claimId)) {
            latestTransmissionsByClaimId.set(claimId, null);
        }

        return;
    }

    const existing = latestTransmissionsByClaimId.get(claimId);

    if (!existing || getCreatedTime(latestTransmission.created) > getCreatedTime(existing.created)) {
        latestTransmissionsByClaimId.set(claimId, latestTransmission);
    }
}

export function mapLatestTransmissionStatusesToClaimIds(
    transmissions: ClaimTransmission[],
    requestedClaimIds: string[],
) {
    const latestTransmissionsByClaimId = new Map<string, LatestTransmissionStatus | null>();
    const requestedClaimIdSet = new Set(requestedClaimIds);

    requestedClaimIds.forEach(claimId => {
        latestTransmissionsByClaimId.set(claimId, null);
    });

    transmissions.forEach(transmission => {
        const claimIdsFromPayload = getRequestedClaimIdsFromTransmission(transmission, requestedClaimIdSet);
        const claimIds =
            claimIdsFromPayload.length > 0
                ? claimIdsFromPayload
                : requestedClaimIds.length === 1
                  ? requestedClaimIds
                  : [];
        const latestTransmission = getLatestTransmissionStatus([transmission]);

        claimIds.forEach(claimId => {
            mergeLatestTransmissionStatus(latestTransmissionsByClaimId, claimId, latestTransmission);
        });
    });

    return latestTransmissionsByClaimId;
}

function getRequestedClaimIdsFromTransmission(transmission: ClaimTransmission, requestedClaimIdSet: Set<string>) {
    return Array.from(getStringValuesByKey(transmission, 'claimId')).filter(claimId =>
        requestedClaimIdSet.has(claimId),
    );
}

function getMatchedClaimIdsFromTransmissions(transmissions: ClaimTransmission[], requestedClaimIds: string[]) {
    const requestedClaimIdSet = new Set(requestedClaimIds);
    const matchedClaimIds = new Set<string>();

    transmissions.forEach(transmission => {
        getRequestedClaimIdsFromTransmission(transmission, requestedClaimIdSet).forEach(claimId => {
            matchedClaimIds.add(claimId);
        });
    });

    return matchedClaimIds;
}

export async function fetchCoderRules(apiBaseUrl: string, endpoint: DashboardEndpoint, accessToken: string) {
    const response = await fetch(buildApiUrl(apiBaseUrl, `/${endpoint}/coding/rules`), {
        headers: {
            accept: 'application/json',
            authorization: `Bearer ${accessToken}`,
        },
    });

    if (!response.ok) {
        throw new Error(await readError(response));
    }

    const payload = await response.json();

    if (!Array.isArray(payload)) {
        throw new Error('Coder rules response was not a list.');
    }

    return payload as CoderRule[];
}

function normalizeCoderRuleClaim(value: unknown): CoderRuleClaim | null {
    if (typeof value === 'string') {
        const claimId = value.trim();

        return claimId ? { claimId } : null;
    }

    if (!value || typeof value !== 'object') {
        return null;
    }

    const rawClaim = value as Partial<CoderRuleClaim>;
    const claimId = typeof rawClaim.claimId === 'string' ? rawClaim.claimId.trim() : '';
    const runDate = typeof rawClaim.runDate === 'string' ? rawClaim.runDate.trim() : undefined;

    return claimId
        ? {
              claimId,
              runDate: runDate || undefined,
          }
        : null;
}

export async function fetchCoderRuleClaims(
    apiBaseUrl: string,
    endpoint: DashboardEndpoint,
    accessToken: string,
    ruleNumber: number,
    onRetry?: (attemptNumber: number, totalAttemptCount: number, reason: string) => void,
) {
    let response: Response | null = null;
    let lastFetchError: unknown;
    const totalAttemptCount = RULE_CLAIM_RETRY_COUNT + 1;

    for (let attemptIndex = 0; attemptIndex < totalAttemptCount; attemptIndex += 1) {
        try {
            response = await fetch(
                buildApiUrl(apiBaseUrl, `/${endpoint}/coding/rules/${encodeURIComponent(String(ruleNumber))}/claim`),
                {
                    headers: {
                        accept: 'application/json',
                        authorization: `Bearer ${accessToken}`,
                    },
                },
            );
            lastFetchError = undefined;
        } catch (caughtError) {
            response = null;
            lastFetchError = caughtError;
        }

        if (response?.ok) {
            break;
        }

        const retryReason = response
            ? `${response.status} ${response.statusText}`.trim()
            : lastFetchError instanceof Error
              ? lastFetchError.message
              : 'Network request failed';
        const hasAttemptsRemaining = attemptIndex < totalAttemptCount - 1;

        if (!hasAttemptsRemaining || (response && !isRetryableStatus(response.status))) {
            break;
        }

        onRetry?.(attemptIndex + 2, totalAttemptCount, retryReason);
        await delay(RULE_CLAIM_RETRY_DELAY_MS * (attemptIndex + 1));
    }

    if (!response?.ok) {
        if (lastFetchError instanceof Error) {
            throw lastFetchError;
        }

        if (!response) {
            throw new Error(`Coder rule ${ruleNumber} claim request did not return a response.`);
        }

        throw new Error(await readError(response));
    }

    const payload = await response.json();

    if (!Array.isArray(payload)) {
        throw new Error(`Coder rule ${ruleNumber} claim response was not a list.`);
    }

    return payload.map(normalizeCoderRuleClaim).filter((claim): claim is CoderRuleClaim => claim !== null);
}

export async function fetchCoderRuleClaimIds(
    apiBaseUrl: string,
    endpoint: DashboardEndpoint,
    accessToken: string,
    ruleNumber: number,
) {
    const claims = await fetchCoderRuleClaims(apiBaseUrl, endpoint, accessToken, ruleNumber);

    return claims.map(claim => claim.claimId);
}

function mergeLatestCodingByClaimId(latestCodingsByClaimId: Map<string, DashboardCoding>, coding: DashboardCoding) {
    const claimId = coding.claimId?.trim();

    if (!claimId) {
        return;
    }

    const existing = latestCodingsByClaimId.get(claimId);

    if (!existing || getCreatedTime(coding.created) > getCreatedTime(existing.created)) {
        latestCodingsByClaimId.set(claimId, coding);
    }
}

function equalsFilter(value: string) {
    return `EQ:${value}`;
}

async function fetchLatestCodingsForClaimBatch(
    apiBaseUrl: string,
    endpoint: DashboardEndpoint,
    accessToken: string,
    claimIds: string[],
) {
    const latestCodingsByClaimId = new Map<string, DashboardCoding>();
    let offset = 0;
    let total: number | null = null;

    while (true) {
        const searchParams = new URLSearchParams({
            limit: String(CODING_PAGE_LIMIT),
            offset: String(offset),
            sort: 'CREATED',
            order: 'DESCENDING',
        });

        claimIds.forEach(claimId => {
            searchParams.append('filterClaimId', equalsFilter(claimId));
        });

        const response = await fetch(buildApiUrl(apiBaseUrl, `/${endpoint}/coding?${searchParams.toString()}`), {
            headers: {
                accept: 'application/json',
                authorization: `Bearer ${accessToken}`,
            },
        });

        if (!response.ok) {
            throw new Error(await readError(response));
        }

        const page = await response.json();

        if (!Array.isArray(page)) {
            throw new Error('Coding response was not a list.');
        }

        (page as DashboardCoding[]).forEach(coding => mergeLatestCodingByClaimId(latestCodingsByClaimId, coding));

        const totalHeader = response.headers.get('Pagination-Total');
        total = totalHeader ? Number(totalHeader) : total;

        if (page.length < CODING_PAGE_LIMIT) {
            break;
        }

        offset += CODING_PAGE_LIMIT;

        if (total !== null && offset >= total) {
            break;
        }
    }

    return latestCodingsByClaimId;
}

export async function fetchLatestCodingsForClaims(
    apiBaseUrl: string,
    endpoint: DashboardEndpoint,
    accessToken: string,
    claimIds: string[],
    onProgress?: (completedBatchCount: number, totalBatchCount: number) => void,
    onBatchLoaded?: (latestCodingsByClaimId: Map<string, DashboardCoding>) => void,
    options: FetchLatestCodingsForClaimsOptions = {},
) {
    const latestCodingsByClaimId = new Map<string, DashboardCoding>();
    const batchSize = Math.max(1, Math.floor(options.batchSize || CLAIM_CODING_BATCH_SIZE));
    const concurrency = Math.max(1, Math.floor(options.concurrency || CLAIM_CODING_CONCURRENCY));
    const claimIdBatches = chunkItems(Array.from(new Set(claimIds)), batchSize);

    const batchResults = await mapWithConcurrency(
        claimIdBatches,
        concurrency,
        async claimIdBatch => {
            try {
                const batchResult = await fetchLatestCodingsForClaimBatch(
                    apiBaseUrl,
                    endpoint,
                    accessToken,
                    claimIdBatch,
                );
                onBatchLoaded?.(batchResult);

                return batchResult;
            } catch {
                await delay(CODING_BATCH_ERROR_DELAY_MS);
                return new Map<string, DashboardCoding>();
            }
        },
        onProgress,
    );

    batchResults.forEach(batchResult => {
        batchResult.forEach(coding => mergeLatestCodingByClaimId(latestCodingsByClaimId, coding));
    });

    return latestCodingsByClaimId;
}

export async function fetchLatestTransmissionStatusForClaim(
    apiBaseUrl: string,
    endpoint: DashboardEndpoint,
    accessToken: string,
    claimId: string,
) {
    const latestTransmissionsByClaimId = await fetchLatestTransmissionStatusesForClaimBatch(
        apiBaseUrl,
        endpoint,
        accessToken,
        [claimId],
    );

    return latestTransmissionsByClaimId.get(claimId) ?? null;
}

async function fetchLatestTransmissionStatusesForClaimBatchPage(
    apiBaseUrl: string,
    endpoint: DashboardEndpoint,
    accessToken: string,
    claimIds: string[],
): Promise<FetchLatestTransmissionStatusesBatchResult> {
    const searchParams = new URLSearchParams({
        limit: String(TRANSMISSION_PAGE_LIMIT),
        offset: '0',
        sort: 'CREATED',
        order: 'DESCENDING',
    });

    claimIds.forEach(claimId => {
        searchParams.append('filterClaimId', equalsFilter(claimId));
    });

    let response: Response | null = null;

    for (let attemptIndex = 0; attemptIndex <= TRANSMISSION_RETRY_COUNT; attemptIndex += 1) {
        response = await fetch(buildApiUrl(apiBaseUrl, `/${endpoint}/transmission?${searchParams.toString()}`), {
            headers: {
                accept: 'application/json',
                authorization: `Bearer ${accessToken}`,
            },
        });

        if (response.ok || !isRetryableStatus(response.status) || attemptIndex === TRANSMISSION_RETRY_COUNT) {
            break;
        }

        await delay(TRANSMISSION_RETRY_DELAY_MS * (attemptIndex + 1));
    }

    if (!response?.ok) {
        if (!response) {
            throw new Error('Transmission request did not return a response.');
        }

        throw new Error(await readError(response));
    }

    const payload = await response.json();

    if (!Array.isArray(payload)) {
        throw new Error('Transmission response was not a list.');
    }

    const transmissions = payload as ClaimTransmission[];

    return {
        isPageFull: transmissions.length >= TRANSMISSION_PAGE_LIMIT,
        latestTransmissionsByClaimId: mapLatestTransmissionStatusesToClaimIds(transmissions, claimIds),
        matchedClaimIds: getMatchedClaimIdsFromTransmissions(transmissions, claimIds),
        transmissionCount: transmissions.length,
    };
}

async function fetchLatestTransmissionStatusesForClaimBatch(
    apiBaseUrl: string,
    endpoint: DashboardEndpoint,
    accessToken: string,
    claimIds: string[],
) {
    const batchResult = await fetchLatestTransmissionStatusesForClaimBatchPage(
        apiBaseUrl,
        endpoint,
        accessToken,
        claimIds,
    );

    return batchResult.latestTransmissionsByClaimId;
}

async function fetchLatestTransmissionStatusesForClaimBatchWithFallback(
    apiBaseUrl: string,
    endpoint: DashboardEndpoint,
    accessToken: string,
    claimIds: string[],
    fallbackConcurrency = TRANSMISSION_FALLBACK_CONCURRENCY,
) {
    try {
        const batchResult = await fetchLatestTransmissionStatusesForClaimBatchPage(
            apiBaseUrl,
            endpoint,
            accessToken,
            claimIds,
        );

        const hasAmbiguousBatchMatches =
            claimIds.length > 1 &&
            batchResult.transmissionCount > 0 &&
            batchResult.matchedClaimIds.size === 0 &&
            Array.from(batchResult.latestTransmissionsByClaimId.values()).every(
                latestTransmission => latestTransmission === null,
            );

        if ((!batchResult.isPageFull && !hasAmbiguousBatchMatches) || claimIds.length <= 1) {
            return batchResult.latestTransmissionsByClaimId;
        }

        const unresolvedClaimIds = claimIds.filter(claimId => !batchResult.matchedClaimIds.has(claimId));

        if (unresolvedClaimIds.length === 0) {
            return batchResult.latestTransmissionsByClaimId;
        }

        const fallbackResults = await mapWithConcurrency(unresolvedClaimIds, fallbackConcurrency, async claimId => {
            try {
                return await fetchLatestTransmissionStatusesForClaimBatch(apiBaseUrl, endpoint, accessToken, [claimId]);
            } catch {
                return new Map<string, LatestTransmissionStatus | null>([[claimId, null]]);
            }
        });

        fallbackResults.forEach(fallbackResult => {
            fallbackResult.forEach((latestTransmission, claimId) => {
                mergeLatestTransmissionStatus(batchResult.latestTransmissionsByClaimId, claimId, latestTransmission);
            });
        });

        return batchResult.latestTransmissionsByClaimId;
    } catch (caughtError) {
        if (claimIds.length <= 1) {
            throw caughtError;
        }

        const latestTransmissionsByClaimId = new Map<string, LatestTransmissionStatus | null>();

        claimIds.forEach(claimId => {
            latestTransmissionsByClaimId.set(claimId, null);
        });

        const fallbackResults = await mapWithConcurrency(claimIds, fallbackConcurrency, async claimId => {
            try {
                return await fetchLatestTransmissionStatusesForClaimBatch(apiBaseUrl, endpoint, accessToken, [claimId]);
            } catch {
                return new Map<string, LatestTransmissionStatus | null>([[claimId, null]]);
            }
        });

        fallbackResults.forEach(fallbackResult => {
            fallbackResult.forEach((latestTransmission, claimId) => {
                mergeLatestTransmissionStatus(latestTransmissionsByClaimId, claimId, latestTransmission);
            });
        });

        return latestTransmissionsByClaimId;
    }
}

function chunkItems<T>(items: T[], chunkSize: number) {
    const chunks: T[][] = [];

    for (let index = 0; index < items.length; index += chunkSize) {
        chunks.push(items.slice(index, index + chunkSize));
    }

    return chunks;
}

export async function fetchLatestTransmissionStatusesForClaims(
    apiBaseUrl: string,
    endpoint: DashboardEndpoint,
    accessToken: string,
    claimIds: string[],
    onProgress?: (completedBatchCount: number, totalBatchCount: number) => void,
    onBatchLoaded?: (latestTransmissionsByClaimId: Map<string, LatestTransmissionStatus | null>) => void,
    options: FetchLatestTransmissionStatusesForClaimsOptions = {},
) {
    const uniqueClaimIds = Array.from(new Set(claimIds));
    const latestTransmissionsByClaimId = new Map<string, LatestTransmissionStatus | null>();
    const batchSize = Math.max(1, Math.floor(options.batchSize || TRANSMISSION_CLAIM_BATCH_SIZE));
    const concurrency = Math.max(1, Math.floor(options.concurrency || TRANSMISSION_CONCURRENCY));
    const fallbackConcurrency = Math.max(
        1,
        Math.floor(options.fallbackConcurrency || TRANSMISSION_FALLBACK_CONCURRENCY),
    );

    uniqueClaimIds.forEach(claimId => {
        latestTransmissionsByClaimId.set(claimId, null);
    });

    const claimIdBatches = chunkItems(uniqueClaimIds, batchSize);
    let failedBatchCount = 0;

    const batchResults = await mapWithConcurrency(
        claimIdBatches,
        concurrency,
        async claimIdBatch => {
            try {
                const batchResult = await fetchLatestTransmissionStatusesForClaimBatchWithFallback(
                    apiBaseUrl,
                    endpoint,
                    accessToken,
                    claimIdBatch,
                    fallbackConcurrency,
                );
                onBatchLoaded?.(batchResult);

                return batchResult;
            } catch {
                failedBatchCount += 1;
                await delay(TRANSMISSION_BATCH_ERROR_DELAY_MS);
                return new Map<string, LatestTransmissionStatus | null>();
            }
        },
        onProgress,
    );

    batchResults.forEach(batchResult => {
        batchResult.forEach((latestTransmission, claimId) => {
            mergeLatestTransmissionStatus(latestTransmissionsByClaimId, claimId, latestTransmission);
        });
    });

    return {
        failedBatchCount,
        latestTransmissionsByClaimId,
        totalBatchCount: claimIdBatches.length,
    };
}

async function mapWithConcurrency<T, R>(
    items: T[],
    concurrency: number,
    mapper: (item: T, index: number) => Promise<R>,
    onProgress?: (completedCount: number, totalCount: number) => void,
) {
    const results: R[] = new Array(items.length);
    let currentIndex = 0;
    let completedCount = 0;
    const workerCount = Math.min(concurrency, items.length);

    async function runWorker() {
        while (currentIndex < items.length) {
            const itemIndex = currentIndex;
            currentIndex += 1;
            results[itemIndex] = await mapper(items[itemIndex], itemIndex);
            completedCount += 1;
            onProgress?.(completedCount, items.length);
        }
    }

    await Promise.all(Array.from({ length: workerCount }, runWorker));

    return results;
}

function buildClaimActivitiesByRule(
    rules: CoderRule[],
    codings: DashboardCoding[],
    latestTransmissionsByClaimId: Map<string, LatestTransmissionStatus | null>,
    now = Date.now(),
) {
    const claimsByRule = new Map<number, Map<string, ClaimRuleActivity>>();

    rules.forEach(rule => {
        if (typeof rule.ruleNumber === 'number') {
            claimsByRule.set(rule.ruleNumber, new Map());
        }
    });

    codings.forEach(coding => {
        const claimId = coding.claimId?.trim();

        if (!claimId) {
            return;
        }

        if (!isClaimAtLeastAWeekOld(coding.created, now)) {
            return;
        }

        extractRuleNumbersFromCoding(coding).forEach(ruleNumber => {
            const ruleClaims = claimsByRule.get(ruleNumber);

            if (!ruleClaims) {
                return;
            }

            const existing = ruleClaims.get(claimId);

            if (existing && getCreatedTime(existing.codingCreated) > getCreatedTime(coding.created)) {
                return;
            }

            ruleClaims.set(claimId, {
                claimId,
                userId: getCodingUserId(coding),
                codingId: coding.id,
                codingCreated: coding.created,
                latestTransmission: latestTransmissionsByClaimId.get(claimId) ?? null,
            });
        });
    });

    return claimsByRule;
}

function sortClaimActivitiesByNewestCoding(claims: ClaimRuleActivity[]) {
    return claims.sort((left, right) => getCreatedTime(right.codingCreated) - getCreatedTime(left.codingCreated));
}

function getSampledClaimsByRule(
    rules: CoderRule[],
    codings: DashboardCoding[],
    latestTransmissionsByClaimId: Map<string, LatestTransmissionStatus | null>,
    sampleSize = RULE_CLAIM_SAMPLE_SIZE,
    now = Date.now(),
) {
    const claimsByRule = buildClaimActivitiesByRule(rules, codings, latestTransmissionsByClaimId, now);
    const sampledClaimsByRule = new Map<number, ClaimRuleActivity[]>();

    rules.forEach(rule => {
        if (typeof rule.ruleNumber !== 'number') {
            return;
        }

        sampledClaimsByRule.set(
            rule.ruleNumber,
            sortClaimActivitiesByNewestCoding(Array.from(claimsByRule.get(rule.ruleNumber)?.values() || [])).slice(
                0,
                sampleSize,
            ),
        );
    });

    return sampledClaimsByRule;
}

export function getSampledClaimIdsForRules(
    rules: CoderRule[],
    codings: DashboardCoding[],
    sampleSize = RULE_CLAIM_SAMPLE_SIZE,
    now = Date.now(),
) {
    const sampledClaimsByRule = getSampledClaimsByRule(rules, codings, new Map(), sampleSize, now);
    const sampledClaimIds = new Set<string>();

    sampledClaimsByRule.forEach(claims => {
        claims.forEach(claim => sampledClaimIds.add(claim.claimId));
    });

    return Array.from(sampledClaimIds);
}

export function summarizeCoderRules(
    rules: CoderRule[],
    codings: DashboardCoding[],
    latestTransmissionsByClaimId: Map<string, LatestTransmissionStatus | null>,
    sampleSize = RULE_CLAIM_SAMPLE_SIZE,
    now = Date.now(),
) {
    const sampledClaimsByRule = getSampledClaimsByRule(rules, codings, latestTransmissionsByClaimId, sampleSize, now);

    return rules
        .map(rule => {
            const claims = typeof rule.ruleNumber === 'number' ? sampledClaimsByRule.get(rule.ruleNumber) || [] : [];

            const successCount = claims.filter(claim =>
                SUCCESS_TRANSMISSION_STATUSES.has(claim.latestTransmission?.status || ''),
            ).length;
            const failedCount = claims.filter(claim =>
                FAILED_TRANSMISSION_STATUSES.has(claim.latestTransmission?.status || ''),
            ).length;
            const missingTransmissionCount = claims.filter(claim => !claim.latestTransmission?.status).length;
            const pendingCount = Math.max(claims.length - successCount - failedCount - missingTransmissionCount, 0);
            const transmittedClaimCount = successCount + failedCount;
            const successRate = transmittedClaimCount === 0 ? null : (successCount / transmittedClaimCount) * 100;

            return {
                ...rule,
                claims,
                failedCount,
                missingTransmissionCount,
                pendingCount,
                successCount,
                successRate,
                transmittedClaimCount,
            } satisfies CoderRuleSummary;
        })
        .sort((left, right) => {
            if (left.isActive !== right.isActive) {
                return left.isActive ? -1 : 1;
            }

            return (left.ruleNumber || 0) - (right.ruleNumber || 0);
        });
}

function normalizeCoderRuleForUser(rule: CoderRule, index: number, userId?: string, userName = DEFAULT_USER_NAME) {
    const ruleNumber = coerceRuleNumber(rule.ruleNumber);
    const normalizedRule: CoderRule = {
        ...rule,
        ownerId: rule.ownerId || userId || undefined,
        ownerName: rule.ownerName || userName,
        ruleKey:
            rule.ruleKey ||
            [userId || userName, ruleNumber === null ? 'unknown-rule' : `rule-${ruleNumber}`, index].join('-'),
        ruleNumber: ruleNumber === null ? undefined : ruleNumber,
    };

    return normalizedRule;
}

function dedupeCoderRuleClaimsByClaimId(claims: CoderRuleClaim[]) {
    const claimsById = new Map<string, CoderRuleClaim>();

    claims.forEach(claim => {
        const claimId = claim.claimId.trim();

        if (!claimId) {
            return;
        }

        const normalizedClaim = { ...claim, claimId };
        const existing = claimsById.get(claimId);

        if (!existing || getCreatedTime(normalizedClaim.runDate) > getCreatedTime(existing.runDate)) {
            claimsById.set(claimId, normalizedClaim);
        }
    });

    return Array.from(claimsById.values());
}

function sortCoderRuleClaimsByNewestRunDate(claims: CoderRuleClaim[]) {
    return [...claims].sort((left, right) => getCreatedTime(right.runDate) - getCreatedTime(left.runDate));
}

function isRuleClaimRunAtLeastAWeekOld(claim: CoderRuleClaim, now = Date.now()) {
    const runTime = getCreatedTime(claim.runDate);

    return runTime > 0 && runTime <= now - CLAIM_MINIMUM_AGE_MS;
}

function getSampledCoderRuleClaims(claims: CoderRuleClaim[], sampleSize: number, now = Date.now()) {
    return sortCoderRuleClaimsByNewestRunDate(claims)
        .filter(claim => isRuleClaimRunAtLeastAWeekOld(claim, now))
        .slice(0, sampleSize);
}

function buildClaimActivitiesFromApiClaims(
    claims: CoderRuleClaim[],
    latestTransmissionsByClaimId: Map<string, LatestTransmissionStatus | null>,
    sampleSize: number,
    now = Date.now(),
) {
    return getSampledCoderRuleClaims(claims, sampleSize, now).map(claim => ({
        claimId: claim.claimId,
        runDate: claim.runDate,
        latestTransmission: latestTransmissionsByClaimId.get(claim.claimId) ?? null,
    }));
}

function buildCoderRuleSummariesFromApi(
    rules: CoderRule[],
    claimsByRuleNumber: Map<number, CoderRuleClaim[]>,
    latestTransmissionsByClaimId: Map<string, LatestTransmissionStatus | null>,
    sampleSize: number,
    now = Date.now(),
) {
    return rules
        .map(rule => {
            const claims =
                typeof rule.ruleNumber === 'number'
                    ? buildClaimActivitiesFromApiClaims(
                          claimsByRuleNumber.get(rule.ruleNumber) || [],
                          latestTransmissionsByClaimId,
                          sampleSize,
                          now,
                      )
                    : [];
            const successCount = claims.filter(claim =>
                SUCCESS_TRANSMISSION_STATUSES.has(claim.latestTransmission?.status || ''),
            ).length;
            const failedCount = claims.filter(claim =>
                FAILED_TRANSMISSION_STATUSES.has(claim.latestTransmission?.status || ''),
            ).length;
            const missingTransmissionCount = claims.filter(claim => !claim.latestTransmission?.status).length;
            const pendingCount = Math.max(claims.length - successCount - failedCount - missingTransmissionCount, 0);
            const transmittedClaimCount = successCount + failedCount;
            const successRate = transmittedClaimCount === 0 ? null : (successCount / transmittedClaimCount) * 100;

            return {
                ...rule,
                claims,
                failedCount,
                missingTransmissionCount,
                pendingCount,
                successCount,
                successRate,
                transmittedClaimCount,
            } satisfies CoderRuleSummary;
        })
        .sort((left, right) => {
            if (left.isActive !== right.isActive) {
                return left.isActive ? -1 : 1;
            }

            return (left.ruleNumber || 0) - (right.ruleNumber || 0);
        });
}

function buildApiDashboardResponseFromRuleSummaries(
    ruleSummaries: CoderRuleSummary[],
    generatedTime: number,
    sampleSize: number,
): ApiCoderRulesDashboardResponse {
    const sampledClaimIds = new Set<string>();

    ruleSummaries.forEach(rule => {
        rule.claims.forEach(claim => sampledClaimIds.add(claim.claimId));
    });

    const successCount = ruleSummaries.reduce((count, rule) => count + rule.successCount, 0);
    const transmittedCount = ruleSummaries.reduce((count, rule) => count + rule.transmittedClaimCount, 0);

    return {
        activeRuleCount: ruleSummaries.filter(rule => rule.isActive).length,
        generatedAt: new Date(generatedTime).toISOString(),
        matchedClaimCount: sampledClaimIds.size,
        overallSuccessRate: transmittedCount === 0 ? null : (successCount / transmittedCount) * 100,
        overallTransmittedCount: transmittedCount,
        ruleCount: ruleSummaries.length,
        rules: ruleSummaries,
        sampleSize,
    };
}

function getDashboardClaimActivity(item: CoderRulesDashboardItem): ClaimRuleActivity | null {
    const claimId = item.claimId?.trim();

    if (!claimId) {
        return null;
    }

    const latestTransmission =
        item.transmissionId ||
        item.transmissionCreateDate ||
        item.latestTransmissionStatus ||
        item.latestTransmissionStatusMessage
            ? {
                  transmissionId: item.transmissionId,
                  status: item.latestTransmissionStatus?.trim().toUpperCase() || undefined,
                  message: item.latestTransmissionStatusMessage?.trim() || undefined,
                  created: item.transmissionCreateDate,
              }
            : null;

    return {
        claimId,
        runDate: item.runDate,
        latestTransmission,
    };
}

function shouldReplaceDashboardClaim(existing: ClaimRuleActivity, nextClaim: ClaimRuleActivity) {
    const existingRunTime = getCreatedTime(existing.runDate);
    const nextRunTime = getCreatedTime(nextClaim.runDate);

    if (existingRunTime !== nextRunTime) {
        return nextRunTime > existingRunTime;
    }

    return getCreatedTime(nextClaim.latestTransmission?.created) > getCreatedTime(existing.latestTransmission?.created);
}

function getRuleSortValue(rule: CoderRuleSummary) {
    return typeof rule.ruleNumber === 'number' ? rule.ruleNumber : Number.MAX_SAFE_INTEGER;
}

function sortCoderRuleSummaries(ruleSummaries: CoderRuleSummary[]) {
    return ruleSummaries.sort((left, right) => {
        if (left.isActive !== right.isActive) {
            return left.isActive ? -1 : 1;
        }

        return getRuleSortValue(left) - getRuleSortValue(right);
    });
}

function getRuleGroupKey(ruleNumber: number | null, fallbackKey: string) {
    return ruleNumber === null ? fallbackKey : `rule-number-${ruleNumber}`;
}

function getLatestRuleRunDate(rule: CoderRuleSummary) {
    return rule.claims.reduce((latestRunDate: string | undefined, claim) => {
        if (!latestRunDate || getCreatedTime(claim.runDate) > getCreatedTime(latestRunDate)) {
            return claim.runDate;
        }

        return latestRunDate;
    }, undefined);
}

function buildEmptyCoderRuleSummary(rule: CoderRule): CoderRuleSummary {
    return {
        ...rule,
        claims: [],
        failedCount: 0,
        missingTransmissionCount: 0,
        pendingCount: 0,
        successCount: 0,
        successRate: null,
        transmittedClaimCount: 0,
    };
}

function buildDashboardResponseFromRules(rules: CoderRule[], generatedTime: number): ApiCoderRulesDashboardResponse {
    const ruleSummaries = sortCoderRuleSummaries(rules.map(buildEmptyCoderRuleSummary));

    return {
        activeRuleCount: ruleSummaries.filter(rule => rule.isActive).length,
        generatedAt: new Date(generatedTime).toISOString(),
        matchedClaimCount: 0,
        overallSuccessRate: null,
        overallTransmittedCount: 0,
        ruleCount: ruleSummaries.length,
        rules: ruleSummaries,
    };
}

function buildDashboardResponseFromItems(
    items: CoderRulesDashboardItem[],
    generatedTime: number,
    userId?: string,
    userName = DEFAULT_USER_NAME,
    rules: CoderRule[] = [],
): ApiCoderRulesDashboardResponse {
    const rulesByKey = new Map<string, CoderRuleSummary>();
    const claimsByRuleKey = new Map<string, Map<string, ClaimRuleActivity>>();

    rules.forEach((rule, index) => {
        const ruleNumber = coerceRuleNumber(rule.ruleNumber);
        const ruleGroupKey = getRuleGroupKey(ruleNumber, rule.ruleKey || `rules-endpoint-rule-${index}`);

        rulesByKey.set(ruleGroupKey, buildEmptyCoderRuleSummary(rule));
        claimsByRuleKey.set(ruleGroupKey, new Map());
    });

    items.forEach((item, index) => {
        const ruleNumber = coerceRuleNumber(item.ruleNumber);
        const description = item.ruleDescription?.trim() || 'No description';
        const ruleGroupKey = getRuleGroupKey(ruleNumber, `dashboard-rule-${index}`);

        if (!rulesByKey.has(ruleGroupKey)) {
            rulesByKey.set(ruleGroupKey, {
                ownerId: userId || undefined,
                ownerName: userName,
                ruleKey: ruleGroupKey,
                ruleNumber: ruleNumber === null ? undefined : ruleNumber,
                description,
                claims: [],
                failedCount: 0,
                missingTransmissionCount: 0,
                pendingCount: 0,
                successCount: 0,
                successRate: null,
                transmittedClaimCount: 0,
            });
            claimsByRuleKey.set(ruleGroupKey, new Map());
        }

        const rule = rulesByKey.get(ruleGroupKey);

        if (rule && rule.description === 'No description' && description !== 'No description') {
            rule.description = description;
        }

        const claim = getDashboardClaimActivity(item);

        if (!claim) {
            return;
        }

        const ruleClaims = claimsByRuleKey.get(ruleGroupKey);
        const existingClaim = ruleClaims?.get(claim.claimId);

        if (!existingClaim || shouldReplaceDashboardClaim(existingClaim, claim)) {
            ruleClaims?.set(claim.claimId, claim);
        }
    });

    const matchedClaimIds = new Set<string>();
    const ruleSummaries = Array.from(rulesByKey.entries()).map(([ruleGroupKey, rule]) => {
        const claims = Array.from(claimsByRuleKey.get(ruleGroupKey)?.values() || []).sort((left, right) => {
            const runDateDifference = getCreatedTime(right.runDate) - getCreatedTime(left.runDate);

            return runDateDifference || left.claimId.localeCompare(right.claimId);
        });
        const successCount = claims.filter(claim =>
            SUCCESS_TRANSMISSION_STATUSES.has(getClaimTransmissionStatus(claim)),
        ).length;
        const failedCount = claims.filter(claim =>
            FAILED_TRANSMISSION_STATUSES.has(getClaimTransmissionStatus(claim)),
        ).length;
        const missingTransmissionCount = claims.filter(
            claim => getClaimTransmissionStatus(claim) === 'NO_STATUS',
        ).length;
        const pendingCount = Math.max(claims.length - successCount - failedCount - missingTransmissionCount, 0);
        const transmittedClaimCount = successCount + failedCount;

        claims.forEach(claim => matchedClaimIds.add(claim.claimId));

        return {
            ...rule,
            claims,
            failedCount,
            latestRunDate: getLatestRuleRunDate({ ...rule, claims }),
            missingTransmissionCount,
            pendingCount,
            successCount,
            successRate: transmittedClaimCount === 0 ? null : (successCount / transmittedClaimCount) * 100,
            transmittedClaimCount,
        } satisfies CoderRuleSummary;
    });
    sortCoderRuleSummaries(ruleSummaries);
    const successCount = ruleSummaries.reduce((count, rule) => count + rule.successCount, 0);
    const transmittedCount = ruleSummaries.reduce((count, rule) => count + rule.transmittedClaimCount, 0);

    return {
        activeRuleCount: ruleSummaries.filter(rule => rule.isActive).length,
        generatedAt: new Date(generatedTime).toISOString(),
        matchedClaimCount: matchedClaimIds.size,
        overallSuccessRate: transmittedCount === 0 ? null : (successCount / transmittedCount) * 100,
        overallTransmittedCount: transmittedCount,
        ruleCount: ruleSummaries.length,
        rules: ruleSummaries,
    };
}

export async function fetchCoderRulesDashboardFromApi({
    accessToken: providedAccessToken,
    apiBaseUrl,
    clientId,
    clientSecret,
    endpoint,
    onDashboardUpdate,
    onProgress,
    userId,
    userName = DEFAULT_USER_NAME,
}: FetchApiCoderRulesDashboardOptions): Promise<ApiCoderRulesDashboardResponse> {
    let accessToken = providedAccessToken?.trim();

    if (!accessToken) {
        if (!clientId?.trim() || !clientSecret?.trim()) {
            throw new Error('Enter API credentials are required when an application session token is not provided.');
        }

        onProgress?.('Generating API token');
        accessToken = await requestAccessToken(apiBaseUrl, clientId, clientSecret);
    }

    const generatedTime = Date.now();

    onProgress?.('Loading coder rules from Enter API');
    const rules = (await fetchCoderRules(apiBaseUrl, endpoint, accessToken)).map((rule, index) =>
        normalizeCoderRuleForUser(rule, index, userId, userName),
    );
    onDashboardUpdate?.(buildDashboardResponseFromRules(rules, generatedTime));

    onProgress?.(`Loaded ${rules.length.toLocaleString()} coder rules. Loading dashboard claim and transmission data`);
    const response = await fetch(buildApiUrl(apiBaseUrl, `/${endpoint}/coding/rules/dashboard`), {
        headers: {
            accept: 'application/json',
            authorization: `Bearer ${accessToken}`,
        },
    });

    if (!response.ok) {
        throw new Error(await readError(response));
    }

    const payload = await response.json();

    if (!Array.isArray(payload)) {
        throw new Error('Coder rules dashboard response was not a list.');
    }

    return buildDashboardResponseFromItems(
        payload as CoderRulesDashboardItem[],
        generatedTime,
        userId,
        userName,
        rules,
    );
}

function CoderRulesDashboard({
    accessToken,
    autoLoad = false,
    apiBaseUrl: initialApiBaseUrl = DEFAULT_API_BASE_URL,
    clientId: initialClientId = DEFAULT_CLIENT_ID,
    clientSecret: initialClientSecret = DEFAULT_CLIENT_SECRET,
    endpoint: initialEndpoint = DEFAULT_ENDPOINT,
    getAccessToken,
    showConnectionControls = true,
    userId: initialUserId = DEFAULT_USER_ID,
    userName: initialUserName = DEFAULT_USER_NAME,
}: CoderRulesDashboardProps) {
    const [apiBaseUrl, setApiBaseUrl] = useState(initialApiBaseUrl);
    const [endpoint, setEndpoint] = useState<DashboardEndpoint>(initialEndpoint);
    const [clientId, setClientId] = useState(initialClientId);
    const [clientSecret, setClientSecret] = useState(initialClientSecret);
    const [userId, setUserId] = useState(initialUserId);
    const [userName, setUserName] = useState(initialUserName);
    const [initialCachedDashboard] = useState(() =>
        readCachedCoderRulesDashboard(
            buildCoderRulesDashboardCacheKey({
                apiBaseUrl: initialApiBaseUrl,
                endpoint: initialEndpoint,
                userId: initialUserId,
                userName: initialUserName,
            }),
        ),
    );
    const [ruleSummaries, setRuleSummaries] = useState<CoderRuleSummary[]>(
        () => initialCachedDashboard?.dashboard.rules || [],
    );
    const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
    const [progress, setProgress] = useState(() =>
        initialCachedDashboard
            ? `Loaded cached coder rules from ${formatDate(initialCachedDashboard.dashboard.generatedAt)}`
            : '',
    );
    const [error, setError] = useState('');
    const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(() =>
        parseDashboardLoadedAt(initialCachedDashboard?.dashboard.generatedAt),
    );
    const [expandedRuleKey, setExpandedRuleKey] = useState<string | null>(null);
    const [claimStatusFiltersByRuleKey, setClaimStatusFiltersByRuleKey] = useState<Record<string, string[]>>({});

    const isBusy = isLoadingDashboard;
    const currentAccessToken = getAccessToken?.() || accessToken;
    const hasApplicationAuth = !!currentAccessToken?.trim();
    const canLoadDashboard =
        apiBaseUrl.trim().length > 0 &&
        (hasApplicationAuth || (clientId.trim().length > 0 && clientSecret.trim().length > 0));
    const activeRulesCount = ruleSummaries.filter(rule => rule.isActive).length;
    const matchedClaimIds = useMemo(() => {
        const claimIds = new Set<string>();
        ruleSummaries.forEach(rule => {
            rule.claims.forEach(claim => claimIds.add(claim.claimId));
        });
        return claimIds;
    }, [ruleSummaries]);
    const overallSuccessCount = ruleSummaries.reduce((count, rule) => count + rule.successCount, 0);
    const overallTransmittedCount = ruleSummaries.reduce((count, rule) => count + rule.transmittedClaimCount, 0);
    const overallSuccessRate =
        overallTransmittedCount === 0 ? null : (overallSuccessCount / overallTransmittedCount) * 100;
    const handleLoadDashboard = useCallback(async () => {
        setIsLoadingDashboard(true);
        setError('');
        setProgress('Preparing Enter API request');
        setExpandedRuleKey(null);

        try {
            const trimmedApiBaseUrl = apiBaseUrl.trim();
            const trimmedUserId = userId.trim();
            const trimmedUserName = userName.trim() || DEFAULT_USER_NAME;
            const dashboardCacheKey = buildCoderRulesDashboardCacheKey({
                apiBaseUrl: trimmedApiBaseUrl,
                endpoint,
                userId: trimmedUserId,
                userName: trimmedUserName,
            });

            setRuleSummaries([]);
            setLastLoadedAt(null);

            const dashboard = await fetchCoderRulesDashboardFromApi({
                accessToken: getAccessToken?.() || accessToken,
                apiBaseUrl: trimmedApiBaseUrl,
                clientId: clientId.trim(),
                clientSecret,
                endpoint,
                onDashboardUpdate: nextDashboard => {
                    setRuleSummaries(nextDashboard.rules || []);
                },
                onProgress: setProgress,
                userId: trimmedUserId || undefined,
                userName: trimmedUserName,
            });

            setRuleSummaries(dashboard.rules || []);
            setLastLoadedAt(parseDashboardLoadedAt(dashboard.generatedAt) || new Date());
            saveCachedCoderRulesDashboard(dashboardCacheKey, dashboard);
            setProgress(
                `Loaded ${(dashboard.ruleCount || dashboard.rules?.length || 0).toLocaleString()} coder rules for ${trimmedUserName} across ${(dashboard.matchedClaimCount || 0).toLocaleString()} claims`,
            );
        } catch (caughtError) {
            const message =
                caughtError instanceof TypeError
                    ? 'Network request failed.'
                    : caughtError instanceof Error
                      ? caughtError.message
                      : 'Unable to load coder rule dashboard.';

            setError(message);
            setProgress('');
        } finally {
            setIsLoadingDashboard(false);
        }
    }, [accessToken, apiBaseUrl, clientId, clientSecret, endpoint, getAccessToken, userId, userName]);

    useEffect(() => {
        if (showConnectionControls) {
            return;
        }

        const trimmedUserId = initialUserId.trim();
        const trimmedUserName = initialUserName.trim() || DEFAULT_USER_NAME;

        setUserId(initialUserId);
        setUserName(initialUserName);
        setApiBaseUrl(initialApiBaseUrl);
        setEndpoint(initialEndpoint);
        setExpandedRuleKey(null);
        setClaimStatusFiltersByRuleKey({});
        setError('');

        const cachedDashboard = readCachedCoderRulesDashboard(
            buildCoderRulesDashboardCacheKey({
                apiBaseUrl: initialApiBaseUrl,
                endpoint: initialEndpoint,
                userId: trimmedUserId,
                userName: trimmedUserName,
            }),
        );

        setRuleSummaries(cachedDashboard?.dashboard.rules || []);
        setLastLoadedAt(parseDashboardLoadedAt(cachedDashboard?.dashboard.generatedAt));
        setProgress(
            cachedDashboard
                ? `Loaded cached coder rules from ${formatDate(cachedDashboard.dashboard.generatedAt)}`
                : '',
        );
    }, [initialApiBaseUrl, initialEndpoint, initialUserId, initialUserName, showConnectionControls]);

    useEffect(() => {
        if (
            autoLoad &&
            canLoadDashboard &&
            !isLoadingDashboard &&
            !lastLoadedAt &&
            ruleSummaries.length === 0 &&
            !error
        ) {
            void handleLoadDashboard();
        }
    }, [
        autoLoad,
        canLoadDashboard,
        error,
        handleLoadDashboard,
        isLoadingDashboard,
        lastLoadedAt,
        ruleSummaries.length,
    ]);

    return (
        <DashboardShell>
            <div className="tab-panel">
                <section className="topbar" aria-labelledby="rules-dashboard-title">
                    <div>
                        <h1 id="rules-dashboard-title">Coder Rules Dashboard</h1>
                    </div>
                    <div className="topbar-actions">
                        <button
                            type="button"
                            disabled={!canLoadDashboard || isBusy}
                            onClick={() => {
                                void handleLoadDashboard();
                            }}
                        >
                            {isLoadingDashboard
                                ? 'Loading rules'
                                : lastLoadedAt
                                  ? 'Refresh dashboard'
                                  : 'Load dashboard'}
                        </button>
                    </div>
                </section>

                <section className="message dashboard-note" aria-label="Rule health note">
                    Claim rejections or denials do not necessarily mean a coder rule is underperforming. A claim can be
                    rejected or denied for reasons unrelated to the rule, so these metrics are intended as directional
                    indicators of rule health.
                </section>

                {showConnectionControls ? (
                    <section className="connection-band" aria-label="Connection">
                        <form
                            className="connection-form rules-connection-form"
                            onSubmit={event => {
                                event.preventDefault();
                                handleLoadDashboard();
                            }}
                        >
                            <label>
                                API base URL
                                <input
                                    onChange={event => setApiBaseUrl(event.target.value)}
                                    placeholder="https://api.enter.health"
                                    type="url"
                                    value={apiBaseUrl}
                                />
                            </label>

                            <label>
                                API endpoint
                                <select
                                    onChange={event => setEndpoint(event.target.value as DashboardEndpoint)}
                                    value={endpoint}
                                >
                                    <option value="support">Support</option>
                                    <option value="provider">Provider</option>
                                </select>
                            </label>

                            <label>
                                Client ID
                                <input
                                    autoComplete="username"
                                    onChange={event => setClientId(event.target.value)}
                                    value={clientId}
                                />
                            </label>

                            <label>
                                Client secret
                                <input
                                    autoComplete="current-password"
                                    onChange={event => setClientSecret(event.target.value)}
                                    type="password"
                                    value={clientSecret}
                                />
                            </label>

                            <label>
                                User ID
                                <input
                                    onChange={event => setUserId(event.target.value)}
                                    placeholder="Optional support link user id"
                                    value={userId}
                                />
                            </label>

                            <label>
                                User name
                                <input onChange={event => setUserName(event.target.value)} value={userName} />
                            </label>
                        </form>

                        <div className="connection-state" aria-live="polite">
                            <span className={lastLoadedAt ? 'state-dot ready' : 'state-dot'} />
                            <div>
                                <strong>{lastLoadedAt ? 'Coder Rules Loaded' : 'Waiting for Coder Rule Data'}</strong>
                                <span>
                                    {lastLoadedAt
                                        ? `Loaded ${formatDate(lastLoadedAt.toISOString())}`
                                        : hasApplicationAuth
                                          ? `Ready to load ${endpoint === 'provider' ? 'provider' : 'support'} API data`
                                          : 'Waiting for application session'}
                                </span>
                            </div>
                        </div>
                    </section>
                ) : null}

                {(error || progress) && (
                    <section className={error ? 'message error-message' : 'message'} aria-live="polite">
                        <span className="message-content">
                            {isLoadingDashboard && !error && <Spinner className="message-spinner" size="16px" />}
                            <span>{error || progress}</span>
                        </span>
                    </section>
                )}

                <section className="summary-grid rules-summary-grid" aria-label="Coder rules summary">
                    <article className="summary-card">
                        <span>Coder rules</span>
                        <strong>{ruleSummaries.length}</strong>
                        <small>{activeRulesCount} active</small>
                    </article>
                    <article className="summary-card">
                        <span>Claims</span>
                        <strong>{matchedClaimIds.size}</strong>
                        <small>Unique claims in returned dashboard rows</small>
                    </article>
                    <article className="summary-card">
                        <span>Overall success rate</span>
                        <strong>{formatPercent(overallSuccessRate, isLoadingDashboard)}</strong>
                        <small>{overallTransmittedCount} claims with success/failure statuses</small>
                    </article>
                    <article className="summary-card">
                        <span>Last loaded</span>
                        <strong className="date-value">{formatDate(lastLoadedAt?.toISOString())}</strong>
                        <small>Uses latest transmission fields returned by the API</small>
                    </article>
                </section>

                <section className="table-section" aria-labelledby="rules-table-title">
                    <div className="table-header">
                        <div>
                            <h2 id="rules-table-title">Coder Rules</h2>
                            <p>
                                Rules are loaded into the table first from the coder rules endpoint. Claim and
                                transmission metrics are filled in from the dashboard endpoint after the rules render.
                                Success compares successful statuses against failed statuses; pending and missing
                                transmissions are excluded.
                            </p>
                        </div>
                    </div>

                    <div className="table-wrap">
                        <table className="rules-table">
                            <thead>
                                <tr>
                                    <th>Rule</th>
                                    <th>Status</th>
                                    <th>Description</th>
                                    <th>Claims</th>
                                    <th>Success Rate</th>
                                    <th>Latest Run</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ruleSummaries.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="empty-state">
                                            No coder rules loaded
                                        </td>
                                    </tr>
                                ) : (
                                    ruleSummaries.map(rule => {
                                        const ruleKey =
                                            rule.ruleKey ||
                                            [rule.ownerId, rule.ruleNumber ?? 'unknown', rule.description]
                                                .filter(Boolean)
                                                .join('-');
                                        const isExpanded = expandedRuleKey === ruleKey;
                                        const claimStatusOptions = getClaimStatusOptions(rule.claims);
                                        const allClaimStatuses = claimStatusOptions.map(option => option.status);
                                        const selectedClaimStatuses = claimStatusFiltersByRuleKey[ruleKey] || [];
                                        const selectedClaimStatusSet = new Set(selectedClaimStatuses);
                                        const filteredClaims =
                                            selectedClaimStatuses.length === 0
                                                ? rule.claims
                                                : rule.claims.filter(claim =>
                                                      selectedClaimStatusSet.has(getClaimTransmissionStatus(claim)),
                                                  );

                                        return (
                                            <React.Fragment key={ruleKey}>
                                                <tr>
                                                    <td className="rule-number-cell">
                                                        {typeof rule.ruleNumber === 'number'
                                                            ? `Rule ${rule.ruleNumber}`
                                                            : 'Rule'}
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={
                                                                rule.isActive === true
                                                                    ? 'status-pill status-completed'
                                                                    : 'status-pill'
                                                            }
                                                        >
                                                            {rule.isActive === true
                                                                ? 'ACTIVE'
                                                                : rule.isActive === false
                                                                  ? 'INACTIVE'
                                                                  : 'UNKNOWN'}
                                                        </span>
                                                    </td>
                                                    <td className="rule-description-cell">
                                                        {rule.description || 'No description'}
                                                    </td>
                                                    <td>{rule.claims.length}</td>
                                                    <td>
                                                        <strong>
                                                            {formatPercent(rule.successRate, isLoadingDashboard)}
                                                        </strong>
                                                        <span className="rate-breakdown">
                                                            {rule.successCount} success, {rule.failedCount} failed,{' '}
                                                            {rule.pendingCount} pending, {rule.missingTransmissionCount}{' '}
                                                            missing
                                                        </span>
                                                    </td>
                                                    <td>{formatDate(rule.latestRunDate)}</td>
                                                    <td>
                                                        <button
                                                            className="secondary-button compact-button"
                                                            disabled={rule.claims.length === 0}
                                                            onClick={() => {
                                                                if (isExpanded) {
                                                                    setExpandedRuleKey(null);
                                                                    return;
                                                                }

                                                                setClaimStatusFiltersByRuleKey(currentFilters => ({
                                                                    ...currentFilters,
                                                                    [ruleKey]: [],
                                                                }));
                                                                setExpandedRuleKey(ruleKey);
                                                            }}
                                                            type="button"
                                                        >
                                                            {isExpanded ? 'Hide claims' : 'View claims'}
                                                        </button>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="rule-claims-row">
                                                        <td colSpan={7}>
                                                            <div className="rule-claims-toolbar">
                                                                <div>
                                                                    <span className="status-filter-label">
                                                                        Transmission Status
                                                                    </span>
                                                                    <div
                                                                        className="status-filter-group"
                                                                        role="group"
                                                                        aria-label="Filter claims by latest transmission status"
                                                                    >
                                                                        {claimStatusOptions.map(({ count, status }) => {
                                                                            const isSelected =
                                                                                selectedClaimStatusSet.has(status);

                                                                            return (
                                                                                <button
                                                                                    aria-pressed={isSelected}
                                                                                    className={`${statusClassName(
                                                                                        status,
                                                                                    )} status-filter-pill${
                                                                                        isSelected ? ' selected' : ''
                                                                                    }`}
                                                                                    key={status}
                                                                                    onClick={() => {
                                                                                        setClaimStatusFiltersByRuleKey(
                                                                                            currentFilters => {
                                                                                                const currentStatuses =
                                                                                                    currentFilters[
                                                                                                        ruleKey
                                                                                                    ] || [];
                                                                                                const nextStatuses =
                                                                                                    currentStatuses.includes(
                                                                                                        status,
                                                                                                    )
                                                                                                        ? currentStatuses.filter(
                                                                                                              item =>
                                                                                                                  item !==
                                                                                                                  status,
                                                                                                          )
                                                                                                        : allClaimStatuses.filter(
                                                                                                              item =>
                                                                                                                  item ===
                                                                                                                      status ||
                                                                                                                  currentStatuses.includes(
                                                                                                                      item,
                                                                                                                  ),
                                                                                                          );

                                                                                                return {
                                                                                                    ...currentFilters,
                                                                                                    [ruleKey]:
                                                                                                        nextStatuses,
                                                                                                };
                                                                                            },
                                                                                        );
                                                                                    }}
                                                                                    type="button"
                                                                                >
                                                                                    {formatTransmissionStatusLabel(
                                                                                        status,
                                                                                    )}
                                                                                    <span className="status-filter-count">
                                                                                        {count}
                                                                                    </span>
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    className="secondary-button compact-button"
                                                                    disabled={selectedClaimStatuses.length === 0}
                                                                    onClick={() =>
                                                                        setClaimStatusFiltersByRuleKey(
                                                                            currentFilters => ({
                                                                                ...currentFilters,
                                                                                [ruleKey]: [],
                                                                            }),
                                                                        )
                                                                    }
                                                                    type="button"
                                                                >
                                                                    Clear statuses
                                                                </button>
                                                            </div>

                                                            {filteredClaims.length === 0 ? (
                                                                <div className="rule-claims-empty">
                                                                    No claims match the selected statuses
                                                                </div>
                                                            ) : (
                                                                <div className="rule-claims-list">
                                                                    {filteredClaims.map(claim => {
                                                                        const status =
                                                                            getClaimTransmissionStatus(claim);
                                                                        const claimUserId =
                                                                            claim.userId || rule.ownerId;
                                                                        const claimUrl = claimUserId
                                                                            ? buildClaimSupportUrl(
                                                                                  claimUserId,
                                                                                  claim.claimId,
                                                                              )
                                                                            : null;

                                                                        return (
                                                                            <article
                                                                                className="rule-claim-card"
                                                                                key={claim.claimId}
                                                                            >
                                                                                <div>
                                                                                    <span>Claim ID</span>
                                                                                    <strong>
                                                                                        {claimUrl ? (
                                                                                            <a
                                                                                                href={claimUrl}
                                                                                                rel="noreferrer"
                                                                                                target="_blank"
                                                                                            >
                                                                                                {claim.claimId}
                                                                                            </a>
                                                                                        ) : (
                                                                                            claim.claimId
                                                                                        )}
                                                                                    </strong>
                                                                                </div>
                                                                                <div>
                                                                                    <span>Rule Run Date</span>
                                                                                    <strong>
                                                                                        {formatDate(
                                                                                            claim.runDate ||
                                                                                                claim.codingCreated,
                                                                                        )}
                                                                                    </strong>
                                                                                </div>
                                                                                <div>
                                                                                    <span>Transmission ID</span>
                                                                                    <strong>
                                                                                        {claim.latestTransmission
                                                                                            ?.transmissionId ||
                                                                                            'Unknown'}
                                                                                    </strong>
                                                                                </div>
                                                                                <div>
                                                                                    <span>Latest Transmission</span>
                                                                                    <strong>
                                                                                        <span
                                                                                            className={statusClassName(
                                                                                                status,
                                                                                            )}
                                                                                        >
                                                                                            {formatTransmissionStatusLabel(
                                                                                                status,
                                                                                            )}
                                                                                        </span>
                                                                                    </strong>
                                                                                </div>
                                                                                <div>
                                                                                    <span>Transmission Created</span>
                                                                                    <strong>
                                                                                        {formatDate(
                                                                                            claim.latestTransmission
                                                                                                ?.created,
                                                                                        )}
                                                                                    </strong>
                                                                                </div>
                                                                                <div className="rule-claim-message">
                                                                                    <span>Transmission Message</span>
                                                                                    <strong>
                                                                                        {claim.latestTransmission
                                                                                            ?.message || 'No message'}
                                                                                    </strong>
                                                                                </div>
                                                                            </article>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </DashboardShell>
    );
}

export default CoderRulesDashboard;
